import addHoursToDate from "@/helpers/addHoursToDate";
import {
  PlacePrediction,
  SearchPlace,
  Suggestion,
} from "@/interfaces/SearchPlace";
import { PlaceDetails } from "@/modules/hotels/interfaces/PlaceDetails";
import { useStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { ref, watch } from "vue";

export const useSearchPlaceStore = defineStore("searchPlace", () => {
  const searchPlaceQuery = ref<string>("");
  const listCountryCodes = ref<string[]>([]);
  const searchPlaceResults = ref<SearchPlace | undefined>();
  const suggestionPlaces = ref<Suggestion[]>([]);
  const suggestedFiltered = ref<PlacePrediction[]>([]);
  const suggestedFilteredEstablishment = ref<PlacePrediction[]>([]);
  const placeDetails = ref<PlaceDetails>();
  const placeSelected = ref<PlacePrediction>();
  const sessionToken = useStorage("googleSessionToken", "");
  const expiresDate = useStorage(
    "googleSessionExpiresAt",
    undefined,
    undefined,
    {
      serializer: {
        read: (v) => (v ? new Date(v) : undefined),
        write: (v) => (v ? v.toISOString() : ""),
      },
    },
  );
  const placesUsedTimes = useStorage("googlePlacesUsedTimes", 0);

  watch(sessionToken, () => {
    expiresDate.value = new Date(addHoursToDate(new Date(), 1));
  });

  return {
    searchPlaceQuery,
    searchPlaceResults,
    sessionToken,
    expiresDate,
    placesUsedTimes,
    listCountryCodes,
    suggestionPlaces,
    placeSelected,
    placeDetails,
    suggestedFiltered,
    suggestedFilteredEstablishment,

    setSearchPlaceQuery(query: string) {
      searchPlaceQuery.value = query;
    },
    setSearchPlaceResults(results: SearchPlace | undefined) {
      searchPlaceResults.value = results;
    },
    setFilteredResultsAirport() {
      suggestedFiltered.value =
        searchPlaceResults.value?.suggestions
          .filter((suggest) =>
            suggest.placePrediction.types?.includes("airport"),
          )
          .map((suggest) => suggest.placePrediction) ?? [];
    },
    setFilteredResultsEstablishment() {
      // Antes solo "tourist_attraction" — un estadio, un centro de
      // exposiciones o una plaza de toros no cargan ese tipo (Google los
      // marca como "establishment"/"point_of_interest"), así que
      // desaparecían de este bucket aunque Google sí los devolviera.
      suggestedFilteredEstablishment.value =
        searchPlaceResults.value?.suggestions
          .filter((suggest) => {
            const types = suggest.placePrediction.types ?? [];
            // Un aeropuerto también carga "establishment"/"point_of_interest"
            // — se excluye acá porque ya tiene su propio bucket
            // ("Aeropuertos") y no queremos que salga duplicado.
            if (types.includes("airport")) return false;
            return (
              types.includes("establishment") ||
              types.includes("point_of_interest")
            );
          })
          .map((suggest) => suggest.placePrediction) ?? [];
    },
    setSelectedPlace(place: PlacePrediction) {
      placeSelected.value = place;
    },
    setPlaceDetails(details: PlaceDetails) {
      placeSelected.value?.placeId === details.id
        ? (placeDetails.value = details)
        : null;
    },
    setNewSessionToken() {
      sessionToken.value = uuidv4();
    },
    resetSessionToken() {
      sessionToken.value = "";
    },
    addPlacesUsedTime() {
      (placesUsedTimes.value as number)++;
    },
    resetUsedTimes() {
      (placesUsedTimes.value as number) = 0;
    },
    resetApiValuesSearch(renewSessionToken: boolean) {
      renewSessionToken ? this.setNewSessionToken() : this.resetSessionToken();
      this.resetUsedTimes();
    },
    resetValues() {
      this.setSuggestionPlaces([]);
      this.setFilteredResultsAirport();
      this.setFilteredResultsEstablishment();
    },
    setListCountryCodes(codes: string[]) {
      // Codes processed

      listCountryCodes.value = codes;
    },
    setSuggestionPlaces(suggestions: Suggestion[]) {
      suggestionPlaces.value = suggestions;
    },
  };
});
